/**
 * Audit - FR-8.5 (AC7).
 *
 * "Lists actor, action, target, timestamp for state changes" is the criterion, and
 * the row shows exactly those four in that order. Beyond it, each row expands to
 * the `before_state` / `after_state` JSONB snapshots the writing services stored -
 * which is what makes a log worth reading rather than merely complete.
 *
 * Three things about this screen are deliberate:
 *
 * 1. **The filter options come from the data** (`entity_types`, `actions` on every
 *    page). `action` and `entity_type` have no CHECK constraint in the schema, so
 *    a hardcoded list here would silently stop offering a value the first time a
 *    service wrote a new one.
 * 2. **`entity_id` is a filter, and it is the useful one.** It is the whole
 *    history of one member, partner or listing - which is what an admin
 *    investigating something actually wants. It is also settable from the URL, so
 *    another screen can link to a record's history.
 * 3. **The action code is shown as well as a prettified label.** The code is the
 *    precise identifier, and hiding it behind a prettifier would make the log
 *    harder to match against `services/audit_service.py`.
 *
 * A null actor is rendered as "Removed admin", not "Unknown": `actor_user_id` is
 * `ON DELETE SET NULL`, so null means the account is gone, and the change they
 * made still stands.
 */

import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  JsonBlock,
  LoadingRows,
  Page,
  Pagination,
  Select,
} from "../components/primitives";
import { fetchAuditLogs, type AuditLogEntry, type AuditLogPage } from "../lib/api/audit";
import { useQuery } from "../lib/hooks";
import { formatDateTime, humanizeAction, relativeDay } from "../lib/format";

const PAGE_SIZE = 50;

export function AuditScreen() {
  const [params, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState<number | null>(null);

  const entityType = params.get("entity_type") ?? "";
  const action = params.get("action") ?? "";
  const entityId = params.get("entity_id") ?? "";
  const fromDate = params.get("from_date") ?? "";
  const toDate = params.get("to_date") ?? "";
  const offset = Number(params.get("offset") ?? 0) || 0;

  /** Every filter change rewrites the query string, so the current view is always
   *  linkable and a refresh keeps it. */
  const apply = useCallback(
    (changes: Record<string, string | number | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === "" || value === 0) next.delete(key);
        else next.set(key, String(value));
      }
      // Any filter change resets paging: staying on page 4 of a narrower result
      // set shows an empty table for no reason.
      if (!("offset" in changes)) next.delete("offset");
      setParams(next, { replace: true });
      setExpanded(null);
    },
    [params, setParams],
  );

  const query = useQuery<AuditLogPage>(
    () =>
      fetchAuditLogs({
        entity_type: entityType || undefined,
        action: action || undefined,
        entity_id: entityId || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [entityType, action, entityId, fromDate, toDate, offset],
  );
  const { data, error, loading, refreshing, reload } = query;

  const filtersActive = Boolean(entityType || action || entityId || fromDate || toDate);

  return (
    <Page>
      <Card>
        <div className="filters">
          <div style={{ width: 180 }}>
            <Field label="Entity">
              {(id) => (
                <Select
                  id={id}
                  value={entityType}
                  onChange={(event) => apply({ entity_type: event.target.value })}
                >
                  <option value="">All</option>
                  {(data?.entity_types ?? []).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div style={{ width: 230 }}>
            <Field label="Action">
              {(id) => (
                <Select
                  id={id}
                  value={action}
                  onChange={(event) => apply({ action: event.target.value })}
                >
                  <option value="">All</option>
                  {(data?.actions ?? []).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div style={{ width: 300 }}>
            <Field label="Record id" hint="A member, partner or listing id.">
              {(id) => (
                <Input
                  id={id}
                  className="input--mono"
                  value={entityId}
                  placeholder="Paste a uuid"
                  onChange={(event) => apply({ entity_id: event.target.value.trim() })}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="From">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={fromDate}
                  onChange={(event) => apply({ from_date: event.target.value })}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="To">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={toDate}
                  onChange={(event) => apply({ to_date: event.target.value })}
                />
              )}
            </Field>
          </div>

          <div className="row row--tight">
            <Button
              onClick={() => {
                apply({ entity_type: null, action: null, entity_id: null, from_date: null, to_date: null });
              }}
              disabled={!filtersActive}
            >
              Clear filters
            </Button>
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={6} columns={4} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No admin changes match these filters."
            // The server's sentence, which names the next action.
            body={data?.message ?? "Try a wider date range, or clear the filters."}
            action={
              filtersActive ? (
                <Button
                  size="sm"
                  onClick={() =>
                    apply({ entity_type: null, action: null, entity_id: null, from_date: null, to_date: null })
                  }
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Admin</th>
                    <th scope="col">Action</th>
                    <th scope="col">Target</th>
                    <th scope="col" style={{ textAlign: "right" }}>
                      Detail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((entry) => (
                    <AuditRow
                      key={entry.id}
                      entry={entry}
                      expanded={expanded === entry.id}
                      onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
                      onFilterEntity={() => apply({ entity_type: entry.entity_type, entity_id: entry.entity_id ?? null })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="changes"
              onChange={(next) => apply({ offset: next })}
            />
          </>
        )}
      </Card>
    </Page>
  );
}

function AuditRow({
  entry,
  expanded,
  onToggle,
  onFilterEntity,
}: {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
  onFilterEntity: () => void;
}) {
  const changed = diffKeys(entry.before_state, entry.after_state);

  return (
    <>
      <tr>
        <td>
          <div className="table__strong">{formatDateTime(entry.created_at)}</div>
          <div className="table__sub">{relativeDay(entry.created_at)}</div>
        </td>
        <td>
          {entry.actor ? (
            <div className="table__strong">{entry.actor.full_name}</div>
          ) : (
            <span className="text-meta">Removed admin</span>
          )}
        </td>
        <td>
          <div className="table__strong">{humanizeAction(entry.action)}</div>
          <div className="table__sub mono">{entry.action}</div>
        </td>
        <td>
          <div className="row row--tight">
            <Badge tone="plain">{entry.entity_type}</Badge>
            {entry.entity_id ? (
              <button
                type="button"
                className="btn btn--quiet mono"
                onClick={onFilterEntity}
                title="Show this record's whole history"
              >
                {entry.entity_id.slice(0, 8)}…
              </button>
            ) : (
              // `settings` rows are keyed by TEXT, and `audit_logs.entity_id` is
              // UUID - so those writes carry the key inside the state snapshots
              // instead of here.
              <span className="text-meta text-small">no id</span>
            )}
          </div>
        </td>
        <td style={{ textAlign: "right" }}>
          <Button size="sm" onClick={onToggle}>
            {expanded ? "Hide" : "Show"}
          </Button>
        </td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={5} style={{ background: "var(--ground)" }}>
            <div className="stack" style={{ gap: 12 }}>
              <div className="row row--between">
                <span className="label">
                  {changed.length > 0 ? `Changed: ${changed.join(", ")}` : "No field-level change recorded"}
                </span>
                <span className="text-small text-meta">
                  {entry.ip_address ? `from ${entry.ip_address}` : "no address recorded"}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                  gap: 12,
                }}
              >
                <div>
                  <span className="label">Before</span>
                  <JsonBlock value={entry.before_state} />
                </div>
                <div>
                  <span className="label">After</span>
                  <JsonBlock value={entry.after_state} />
                </div>
              </div>

              {entry.user_agent ? (
                <p className="text-small text-meta" style={{ margin: 0, overflowWrap: "anywhere" }}>
                  {entry.user_agent}
                </p>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The keys whose values differ between the two snapshots.
 *
 * Computed here rather than server-side because it is purely a rendering aid -
 * different admins look for different things, and the full snapshots are shown
 * either way. Compared on `JSON.stringify` so a nested object counts as changed
 * when any part of it moved.
 */
function diffKeys(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  );
}
